import { useState, useEffect, useCallback } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Fingerprint, Shield, Trash2, Plus, Monitor, Smartphone, Key, RefreshCw, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { startRegistration } from "@simplewebauthn/browser";
const formatDate = (dateStr) => {
  if (!dateStr) return "Never";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const getDeviceIcon = (deviceType) => {
  if (deviceType === "platform" || deviceType === "internal") {
    const ua = navigator.userAgent || "";
    if (/Windows/.test(ua)) return <Monitor className="w-5 h-5 text-blue-500" />;
    if (/iPhone|iPad|Mac/.test(ua)) return <Fingerprint className="w-5 h-5 text-purple-500" />;
    return <Smartphone className="w-5 h-5 text-green-500" />;
  }
  return <Key className="w-5 h-5 text-amber-500" />;
};

const PasskeysTab = ({ users, getAuthHeaders, currentUser }) => {
  const { token } = useAuth();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Admin: manage other users
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [resettingUser, setResettingUser] = useState(false);

  const isAdmin = currentUser?.role === "admin";

  const fetchPasskeys = useCallback(async () => {
    try {
      const res = await apiClient.get(`/passkeys/list`);
      setPasskeys(res.data);
    } catch (err) {
      console.error("Failed to fetch passkeys:", err);
      toast.error("Failed to load passkeys");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  const handleRegisterPasskey = async () => {
    setRegistering(true);
    try {
      const optionsRes = await apiClient.post(
        `/passkeys/registration-options`);
      const { options } = optionsRes.data;

      const credential = await startRegistration({ optionsJSON: options });
      if (!credential) throw new Error("Passkey creation was cancelled");

      const verifyRes = await apiClient.post(
        `/passkeys/registration-verify`,
        {
          id: credential.id,
          rawId: credential.rawId,
          response: {
            clientDataJSON: credential.response.clientDataJSON,
            attestationObject: credential.response.attestationObject,
            transports: credential.response.getTransports?.() || ["internal"],
          },
          type: credential.type,
          clientExtensionResults: credential.clientExtensionResults,
          authenticatorAttachment: credential.authenticatorAttachment,
        }
      );

      if (verifyRes.data.verified) {
        toast.success("Passkey registered successfully");
        fetchPasskeys();
      } else {
        throw new Error("Verification failed");
      }
    } catch (err) {
      if (err.name === "NotAllowedError") {
        toast.error("Passkey setup was cancelled");
      } else if (err.name === "InvalidStateError") {
        toast.info("A passkey already exists on this device");
      } else {
        toast.error(err.response?.data?.detail || err.message || "Failed to register passkey");
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDeletePasskey = async (credentialId) => {
    const idForApi = credentialId.replace("...", "");
    setDeletingId(credentialId);
    try {
      await apiClient.delete(`/passkeys/${encodeURIComponent(idForApi)}`);
      toast.success("Passkey deleted");
      fetchPasskeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete passkey");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdminResetPasskeys = async () => {
    if (!selectedUserId) return;
    setResettingUser(true);
    try {
      const res = await apiClient.delete(
        `/passkeys/admin/reset/${selectedUserId}`
      );
      const name = users?.find(u => u.id === selectedUserId)?.name || "User";
      toast.success(`Reset ${res.data.deleted_count} passkey(s) for ${name}`);
      setSelectedUserId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset passkeys");
    } finally {
      setResettingUser(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="passkeys-tab">
      {/* My Passkeys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Fingerprint className="w-5 h-5" />
            My Passkeys
          </CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPasskeys}
              data-testid="refresh-passkeys-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={handleRegisterPasskey}
              disabled={registering}
              data-testid="register-passkey-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {registering ? "Setting up..." : "Add Passkey"}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground animate-pulse">Loading passkeys...</p>
          ) : passkeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No passkeys registered</p>
              <p className="text-xs mt-1">Add a passkey for faster, more secure sign-ins</p>
            </div>
          ) : (
            <div className="space-y-3">
              {passkeys.map((pk) => (
                <div
                  key={pk.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                  data-testid={`passkey-item-${pk.id}`}
                >
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(pk.device_type)}
                    <div>
                      <p className="text-sm font-medium">
                        {pk.device_name || pk.description || "Passkey"}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          Created: {formatDate(pk.created_at)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Last used: {formatDate(pk.last_used)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {pk.device_type || "platform"}
                    </Badge>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          disabled={deletingId === pk.id}
                          data-testid={`delete-passkey-${pk.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Passkey?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove this passkey from your account. You won't be able to
                            sign in with it anymore. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDeletePasskey(pk.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="confirm-delete-passkey-btn"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin: Reset User Passkeys */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5" />
              Admin: Reset User Passkeys
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              If a user is locked out and can't use their passkey, you can reset all passkeys for their account.
              They'll need to set up a new passkey on their next login.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Select value={selectedUserId || ""} onValueChange={setSelectedUserId}>
                  <SelectTrigger data-testid="admin-user-select">
                    <SelectValue placeholder="Select a user..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(users || [])
                      .filter(u => u.id !== currentUser?.id)
                      .map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.username})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    disabled={!selectedUserId || resettingUser}
                    data-testid="admin-reset-passkeys-btn"
                  >
                    {resettingUser ? "Resetting..." : "Reset Passkeys"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset All Passkeys?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove ALL passkeys for{" "}
                      <strong>
                        {users?.find(u => u.id === selectedUserId)?.name || "this user"}
                      </strong>
                      . They will need to set up a new passkey on their next login.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleAdminResetPasskeys}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid="confirm-admin-reset-btn"
                    >
                      Reset All Passkeys
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PasskeysTab;
